import { newGuid } from '../common/new-guid';
import {
	initializeDismissElements,
	initializeOpenElements,
} from '../dom/initialize-click-elements';
import { EventDispatcher } from '../events/event-dispatcher';
import { Overlay } from './overlay';
import { OverlayOptions } from './overlay-options';

export abstract class OverlayImplementation {
	protected overlay: Overlay | null | undefined;
	private portalElement: HTMLDivElement;

	beforeOpen = new EventDispatcher();
	beforeClose = new EventDispatcher();
	afterOpened = new EventDispatcher();
	afterClosed = new EventDispatcher();

	constructor(protected element: HTMLElement) {
		this.element.id = this.element.id || newGuid();
		this.portalElement = document.createElement('div');
		initializeOpenElements(document, this.element.id, (event, trigger) => {
			event.preventDefault();
			this.toggle(trigger);
		});
	}

	open(trigger?: HTMLElement): void {
		this.createOverlay(trigger).open();
		initializeDismissElements(this.overlay?.portalElement, (event) => {
			event.preventDefault();
			this.close();
		});
	}

	close(): void {
		this.overlay?.close();
	}

	toggle(trigger?: HTMLElement): void {
		this.overlay?.isOpen ? this.close() : this.open(trigger);
	}

	private createOverlay(trigger?: HTMLElement): Overlay {
		if (!this.overlay) {
			this.initializePortal(this.portalElement);

			this.overlay = new Overlay(
				this.portalElement,
				new OverlayOptions(this.getOverlayOptions(trigger))
			).create();

			this.overlay.afterOpened.forward(this.afterOpened);
			this.overlay.afterClosed.forward(this.afterClosed);
			this.overlay.beforeOpen.forward(this.beforeOpen);
			this.overlay.beforeClose.forward(this.beforeClose);

			this.overlay.onDisposed.subscribe(() => {
				this.overlay = null;
			});
		} else {
			this.updatePortalElement(this.portalElement);
		}
		
		return this.overlay;
	}

	abstract getOverlayOptions(trigger?: HTMLElement): OverlayOptions;
	abstract initializePortal(portal: HTMLElement): void;
	protected updatePortalElement(portal: HTMLElement) { return; }
}
